import styled from '@emotion/styled';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { Button, Space } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { useUserCanActivateDirectly } from 'src/lib/customHooks/useUserCanActivateDirectly';
import { useStore } from '../../../store/reducers';
import { IconOk, IconShoppingCart } from '../../BasicComponents/Icons';

interface Props {
  event: Immutable<RacemapEvent>;
  user: Immutable<User_Legacy>;
}

export const PayButton: FC<Props> = ({ event, user }) => {
  const { openPaymentModal } = useStore((s) => s.events.actions);
  const { canActivateDirectly, isLoading } = useUserCanActivateDirectly(user.id);
  const isAllowedToActivate = userIsAllowedToActivate(user, event);

  const content = canActivateDirectly ? (
    <Space>
      <IconOk />
      <div>Activate</div>
    </Space>
  ) : (
    <Space>
      <IconShoppingCart />
      <div>Pay now</div>
    </Space>
  );

  return (
    <>
      <StyledButton
        onClick={openPaymentModal}
        disabled={!isAllowedToActivate || canActivateDirectly == null}
        loading={isLoading}
        title={
          isAllowedToActivate
            ? 'Activate your event now'
            : 'Only the owner of the Tracking Map can pay for it.'
        }
      >
        {content}
      </StyledButton>
    </>
  );
};

// only the owner of the Tracking Map or the child of the owner can activate a event
// and trigger the payment process
const userIsAllowedToActivate = (
  user: Immutable<User_Legacy>,
  event: Immutable<RacemapEvent>,
): boolean => user.id === event.creatorId || user.parentId === event.creatorId;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;

  svg[data-icon='cart-shopping'] {
    margin-right: 5px;
  }
`;
